/**
 * Network page helpers — all pure functions / constants. No React, no
 * hooks, no JSX. Consumed by MyNetwork.jsx and (once split) every section
 * file underneath it.
 *
 * Grouped by concern:
 *   • Avatar / colour:   initialsFrom, COLOR_PALETTE, colorFor, isOnline
 *   • Profile display:   subtitleFor
 *   • Time formatting:   timeAgo, formatTimeSince
 *   • List controls:     SORT_OPTIONS, FILTER_PRESETS, matchesFilter
 */

// ─── Avatar / colour ─────────────────────────────────────────────────

/**
 * Two-letter avatar initials from a full name.
 *
 *   "Amit Sharma"     → "AS"
 *   "ravi   kumar"    → "RK"
 *   "Madonna"         → "M"
 *   ""  / undefined   → "??"
 */
export const initialsFrom = (name) => {
    if (!name) return '??';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || '??';
};

/* Palette = the named tokens PrimeReact themes expose as CSS variables
   (`--blue-100`, `--indigo-100`, etc.). Consumers interpolate these into
   `var(--${color}-100)` style strings. */
export const COLOR_PALETTE = ['blue', 'indigo', 'purple', 'pink', 'teal', 'green', 'orange', 'cyan'];

/* Deterministic colour from a profile id — same person always gets the
   same halo across renders. (Production AgentProfile doesn't carry an
   explicit `color` field; we compute one client-side.) */
export const colorFor = (id) => {
    if (!id) return COLOR_PALETTE[0];
    const n = typeof id === 'number' ? id : String(id).split('').reduce((s, c) => s + c.charCodeAt(0), 0);
    return COLOR_PALETTE[Math.abs(n) % COLOR_PALETTE.length];
};

/* Mocked online-presence indicator — deterministic per profile id so the
   dot doesn't flicker between renders. Two-thirds of profiles read as
   online. Replace with a real `last_active_at` check when the backend
   exposes one. */
export const isOnline = (id) => {
    if (!id) return false;
    const n = typeof id === 'number' ? id : String(id).split('').reduce((s, c) => s + c.charCodeAt(0), 0);
    return Math.abs(n) % 3 !== 0;
};

// ─── Profile display ─────────────────────────────────────────────────

/**
 * Best one-line subtitle from whichever fields the API returned.
 *
 *   Priority:
 *     1. explicit `headline`
 *     2. `current_designation` + `current_company` (joined with ·)
 *     3. `current_company` alone
 *     4. `current_designation` alone
 *     5. ''
 *
 * Older profiles in the production agent_profiles table don't always
 * have `headline` filled in — this fallback chain keeps every row
 * showing *something*.
 */
export const subtitleFor = (p) => {
    if (!p) return '';
    if (p.headline) return p.headline;
    const designation = p.current_designation || p.designation;
    const company = p.current_company || p.company;
    if (designation && company) return `${designation} · ${company}`;
    return company || designation || '';
};

// ─── Time formatting ─────────────────────────────────────────────────

/**
 * Two flavours — different surfaces want different verbosity:
 *
 *   timeAgo(iso)         → "2d", "5h", "3m", "just now"            (compact)
 *   formatTimeSince(iso) → "3 days ago", "yesterday", "since Mar 2025"  (longform)
 */

/* Compact at-a-glance timestamp — used wherever space is tight (e.g.
   top-right corner of a row). */
export const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diff)) return '';
    const m = Math.floor(diff / 60000);
    if (m < 1)  return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7)  return `${d}d`;
    return `${Math.floor(d / 7)}w`;
};

/* Longform variant — used for explicit "X days ago" / "since Mon YYYY"
   meta lines on invitation and connection rows. timeAgo() is for compact
   at-a-glance timestamps; this is for full sentences. */
export const formatTimeSince = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const diff = Date.now() - date.getTime();
    if (diff < 60000) return 'just now';
    const m = Math.floor(diff / 60000);
    if (m < 60)  return `${m} minute${m === 1 ? '' : 's'} ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h} hour${h === 1 ? '' : 's'} ago`;
    const d = Math.floor(h / 24);
    if (d === 1) return 'yesterday';
    if (d < 7)   return `${d} days ago`;
    if (d < 30) {
        const w = Math.floor(d / 7);
        return `${w} week${w === 1 ? '' : 's'} ago`;
    }
    if (d < 365) {
        // For mid-range, render the calendar month (more useful than "5 months ago").
        return `since ${date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
    }
    return `since ${date.getFullYear()}`;
};

// ─── List controls (sort + filter chips) ─────────────────────────────

/* Server-supported order keys, surfaced in the Sort dropdown above the
   connection list. */
export const SORT_OPTIONS = [
    { label: 'Recently added', value: 'recent' },
    { label: 'Name (A–Z)',     value: 'alpha' },
    { label: 'Most mutual',    value: 'mutual' },
];

/* Filter preset chips — common product specialties + metros. The hook
   translates each chip's `match` into backend query params
   (filter_specializations / filter_cities). */
export const FILTER_PRESETS = [
    { label: 'Home Loan',     match: { product_type: 'home_loan' } },
    { label: 'LAP',           match: { product_type: 'property_loan' } },
    { label: 'Personal Loan', match: { product_type: 'personal_loan' } },
    { label: 'Mumbai',        match: { city: 'Mumbai' } },
    { label: 'Bengaluru',     match: { city: 'Bengaluru' } },
    { label: 'Delhi',         match: { city: 'Delhi NCR' } },
];

/* Kept for any client-side filter callers (currently unused after the
   server-side refactor, but exported for parity with the inline version
   that lived in MyNetwork.jsx). */
export const matchesFilter = (party, filter) => {
    if (filter.match?.product_type) {
        const products = (party?.product_expertise || []).map((p) =>
            typeof p === 'object' ? p.product_type : p
        );
        return products.includes(filter.match.product_type);
    }
    if (filter.match?.city) {
        return (party?.location_city || '').toLowerCase().includes(filter.match.city.toLowerCase());
    }
    return false;
};
